import { useEffect, useState } from "react";
import { Trophy, Users, RefreshCw, Pause, ArrowLeftIcon } from "lucide-react";
import { LeaderboardTable } from "../../components/leaderboard/LeaderboardTable";
import { useLeaderboardStore } from "../../store/leaderboardStore";
import { usePublicStore } from "../../store/publicStore";
import { Matches } from "./MatchList";
import { formatDateToDDMMMYYYY } from "../../utils/dateUtils";

export function Leaderboard() {
  const [view, setView] = useState<"group" | "team" | "player">("team");
  const [liveUpdates, setLiveUpdates] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentMatchSetup, setCurrentMatchSetup] = useState<any>();

  const {
    playerLeaderboard,
    getPlayerLeaderboard,
    leaderboardMatchId,
    setLeaderboardMatchId,
  } = useLeaderboardStore();
  const {
    currentSeason,
    getCurrentSeason,
    matchSetups,
    getMatchSetups,
    getAllGroups,
    allGroups,
  } = usePublicStore();

  useEffect(() => {
    if (!currentSeason) {
      getCurrentSeason();
    }
  }, [getCurrentSeason, currentSeason]);

  useEffect(() => {
    getMatchSetups();
  }, [getMatchSetups]);

  useEffect(() => {
    let intervalId: number;

    if (liveUpdates) {
      const fetchData = async () => {
        if (!leaderboardMatchId) return;
        setIsUpdating(true);
        await getPlayerLeaderboard(leaderboardMatchId, true);
        setIsUpdating(false);
      };

      // Initial fetch
      fetchData();

      // Set up interval
      intervalId = window.setInterval(fetchData, 10000);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [liveUpdates, getPlayerLeaderboard, leaderboardMatchId]);

  useEffect(() => {
    if (leaderboardMatchId && currentSeason) {
      getPlayerLeaderboard(leaderboardMatchId);
    }
  }, [getPlayerLeaderboard, leaderboardMatchId, currentSeason]);

  useEffect(() => {
    if (leaderboardMatchId && matchSetups) {
      setCurrentMatchSetup(
        matchSetups.find((match) => match.matchId === leaderboardMatchId)
      );
    }
  }, [leaderboardMatchId, matchSetups]);

  useEffect(() => {
      getAllGroups();
  }, [getAllGroups]);



  return (
    <div className="space-y-6 transition-colors duration-200">
      {leaderboardMatchId && (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <button
              onClick={() => setLeaderboardMatchId(null)}
              className="px-4 py-2 rounded-sm flex items-center space-x-2 text-gray-700 dark:text-gray-300 cursor-pointer hover:text-gray-900 dark:hover:text-white"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Leaderboard {currentMatchSetup?.courseName} - {formatDateToDDMMMYYYY(currentMatchSetup?.date)}
              </h1>
              {isUpdating && (
                <RefreshCw className="w-5 h-5 text-green-600 animate-spin" />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {matchSetups &&
                matchSetups.some(
                  (match) =>
                    match.matchId === leaderboardMatchId &&
                    match.matchInProgress
                ) && (
                  <button
                    onClick={() => setLiveUpdates(!liveUpdates)}
                    className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
                      liveUpdates
                        ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50"
                        : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50"
                    }`}
                  >
                    {liveUpdates ? (
                      <>
                        <Pause className="w-5 h-5" />
                        <span>Stop Live Updates</span>
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        <span>Enable Live Updates</span>
                      </>
                    )}
                  </button>
                )}
              {currentMatchSetup &&
                currentMatchSetup?.groupOrKnockout === "Group" && (
                  <button
                    onClick={() => setView("group")}
                    className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
                      view === "group"
                        ? "bg-green-600 dark:bg-green-700 text-white"
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    }`}
                  >
                    <Trophy className="w-5 h-5" />
                    <span>Group View</span>
                  </button>
                )}
              <button
                onClick={() => setView("team")}
                className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
                  view === "team"
                    ? "bg-green-600 dark:bg-green-700 text-white"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <Trophy className="w-5 h-5" />
                <span>Team View</span>
              </button>
              <button
                onClick={() => setView("player")}
                className={`px-4 py-2 rounded-sm flex items-center space-x-2 ${
                  view === "player"
                    ? "bg-green-600 dark:bg-green-700 text-white"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                <Users className="w-5 h-5" />
                <span>Player View</span>
              </button>
            </div>
          </div>

          <LeaderboardTable
            data={playerLeaderboard}
            view={view}
            groups={allGroups}
            matchSetup={matchSetups.find(
              (match) => match.matchId === leaderboardMatchId
            )}
          />
        </>
      )}
      {!leaderboardMatchId && currentSeason && matchSetups && (
        <Matches
          matches={currentSeason.seasonMatches}
          matchConfigs={matchSetups}
        />
      )}
    </div>
  );
}
